<script setup lang="ts">
export interface IAppMenuBarItem {
	id?: string;
	label: string;
	title?: string;
	rightLabel?: string;
	isChecked?: boolean;
	isDisabled?: boolean;
	isHidden?: boolean;
	isBreakpoint?: boolean;
	isExpand?: boolean;
	children?: any;
	onClick?: (payload?: any) => void;
	onRightClick?: (payload?: any) => void;
	onShiftKeyClick?: (a?: any, payload?: any) => void;
}

const props = withDefaults(
	defineProps<{
		items: IAppMenuBarItem[];
		activeExpandedMenuItem: number | null;
	}>(),
	{
		items: () => [],
		activeExpandedMenuItem: null,
	},
);

const emit = defineEmits([
	"left-click-top-menu-item",
	"right-click-top-menu-item",
	"left-click-sub-menu-item",
	"right-click-sub-menu-item",
	"shift-click-sub-menu-item",
]);

const activeExpandedMenuItem = ref<number | null>(props.activeExpandedMenuItem);
const subMenuActiveMenuItem = ref<number | null>(null);

function filterHiddenItems(items: IAppMenuBarItem[]) {
	if (Array.isArray(items)) {
		return items.filter((item) => !item.isHidden);
	}
	return items;
}

function onMouseUpTopAction(
	evt: MouseEvent,
	action: IAppMenuBarItem,
	index: number,
) {
	if (action.isDisabled) {
		return;
	}

	const isLeftClick = evt.button === 0;
	const isRightClick = evt.button === 2;
	if (isLeftClick) {
		mainClickTopMenu(evt, action, index);
	} else if (isRightClick) {
		altClickTopMenu(evt, action, index);
	} else {
		console.log("Blocked", evt.button);
	}
}
function onMouseUpSubAction(
	evt: MouseEvent,
	action: IAppMenuBarItem,
	index: number,
) {
	if (action.isDisabled) {
		return;
	}

	const isLeftClick = evt.button === 0;
	const isRightClick = evt.button === 2;
	if (isLeftClick) {
		mainClickSubMenu(evt, action, index);
	} else if (isRightClick) {
		altClickSubMenu(evt, action, index);
	} else if (evt.shiftKey && action.onShiftKeyClick) {
		shiftClickSubMenu(evt, action, index);
	} else {
		console.log("Blocked", evt.button);
	}
}
function mainClickTopMenu(evt: Event, action: IAppMenuBarItem, index: number) {
	const payload = {
		action,
		index,
		evt,
	};
	emit("left-click-top-menu-item", payload);

	if (action.isExpand) {
		activeExpandedMenuItem.value =
			activeExpandedMenuItem.value === index ? null : index;
	}

	if (!action.onClick || typeof action.onClick !== "function") {
		if (!action.isExpand) {
			console.warn("Invalid onClick handler, please add it", action);
		}
		return;
	}
	action.onClick(payload);
}
function altClickTopMenu(evt: Event, action: IAppMenuBarItem, index: number) {
	const payload = {
		action,
		index,
		evt,
	};
	emit("right-click-top-menu-item", payload);
	action.onRightClick?.(payload);
}
function shiftClickSubMenu(evt: Event, action: IAppMenuBarItem, index: number) {
	const payload = {
		action,
		index,
		evt,
	};
	emit("shift-click-sub-menu-item", payload);

	// Click handler
	if (!action.onShiftKeyClick || typeof action.onShiftKeyClick !== "function") {
		console.warn("Invalid onShiftKeyClick handler, please add it", action);
		return;
	}
	action.onShiftKeyClick(payload);

	// Closes expanded and doesn't work with more expanded
	activeExpandedMenuItem.value = null;

	subMenuActiveMenuItem.value =
		subMenuActiveMenuItem.value === index ? null : index;
}
function mainClickSubMenu(evt: Event, action: IAppMenuBarItem, index: number) {
	const payload = {
		action,
		index,
		evt,
	};
	emit("left-click-sub-menu-item", payload);

	// Click handler
	if (!action.onClick || typeof action.onClick !== "function") {
		console.warn("Invalid onClick handler, please add it", action);
		return;
	}
	action.onClick(payload);

	// Closes expanded and doesn't work with more expanded
	activeExpandedMenuItem.value = null;

	subMenuActiveMenuItem.value =
		subMenuActiveMenuItem.value === index ? null : index;
}
function altClickSubMenu(evt: Event, action: IAppMenuBarItem, index: number) {
	const payload = {
		action,
		index,
		evt,
	};
	emit("right-click-sub-menu-item", payload);

	// Click handler
	if (!action.onRightClick || typeof action.onRightClick !== "function") {
		return;
	}
	action.onRightClick(payload);

	// Closes expanded and doesn't work with more expanded
	activeExpandedMenuItem.value = null;

	subMenuActiveMenuItem.value =
		subMenuActiveMenuItem.value === index ? null : index;
}
function onMouseEnterTopMenuItem(
	evt: Event,
	action: IAppMenuBarItem,
	index: number,
) {
	if (activeExpandedMenuItem.value !== null) {
		activeExpandedMenuItem.value = index;
	}
}
function addOutsideClickEvent(evt: MouseEvent) {
	const closeAppMenuBarEl = (evt.target as HTMLElement)?.closest(
		".app-menu-bar-list",
	);
	if (!closeAppMenuBarEl) {
		activeExpandedMenuItem.value = null;
		subMenuActiveMenuItem.value = null;
	}
}

onMounted(() => {
	document.addEventListener("mousedown", addOutsideClickEvent);
});

onUnmounted(() => {
	document.removeEventListener("mousedown", addOutsideClickEvent);
});
</script>

<template lang="pug">
.app-menu-bar-list
	.top-action(
		v-for="(action, index) in filterHiddenItems(props.items)",
		:key="index"
	)
		.top-menu-item(
			:class="{ active: activeExpandedMenuItem === index, disabled: action.isDisabled }",
			:disabled="action.isDisabled",
			:title="action.title",
			:tabindex="action.isDisabled ? -1 : 0",
			@mouseenter="onMouseEnterTopMenuItem($event, action, index)",
			@mouseup.prevent="onMouseUpTopAction($event, action, index)",
			@contextmenu.prevent,
			@keydown.enter="mainClickTopMenu($event, action, index)"
		)
			.label {{ action.label }}
		.drop-menu-first-level(
			v-if="activeExpandedMenuItem === index && action.isExpand"
		)
			.drop-menu-action-wrapper(
				v-for="(childItem, childIndex) in filterHiddenItems(action.children)",
				:key="childIndex"
			)
				.drop-menu-item(
					v-if="!childItem.isBreakpoint",
					:class="{ active: subMenuActiveMenuItem === childIndex, disabled: childItem.isDisabled }",
					:disabled="childItem.isDisabled",
					:title="childItem.title",
					:tabindex="childItem.isDisabled ? -1 : 0",
					@mouseup.prevent.stop="onMouseUpSubAction($event, childItem, childIndex)",
					@contextmenu.prevent,
					@keydown.enter="mainClickSubMenu($event, childItem, childIndex)"
				)
					.left-side
						.icon-holder
							span(v-if="childItem.isChecked")
								| ✔️
						.label {{ childItem.label }}
					.right-side
						span {{ childItem.rightLabel }}
						.icon-holder
				.line-break(v-if="childItem.isBreakpoint")
</template>

<style lang="scss" scoped>
.app-menu-bar-list {
	$top-item: 26px;
	$drop-min-wid: 200px;
	$drop-max-wid: 600px;

	display: flex;
	align-items: center;
	color: #f2f2f2;
	font-size: 12px;
	font-weight: 500;

	.top-action {
		user-select: none;
		position: relative;

		.top-menu-item {
			display: flex;
			align-items: center;
			cursor: pointer;
			padding: 3px 10px;
			outline: none;

			&.active {
				background: #fff2;
			}

			&:not(.disabled) {
				cursor: pointer;
				color: inherit;

				&:focus {
					background-color: lighten($color: $medium-gray, $amount: 2);
					outline: 1px solid #656565;
				}

				&:hover {
					background: lighten($color: $medium-gray, $amount: 3);
				}
			}
		}

		.drop-menu-first-level {
			position: absolute;
			left: 0;
			top: $top-item;
			min-width: $drop-min-wid;
			max-width: $drop-max-wid;
			background: darken($color: $medium-gray, $amount: 8);
			z-index: 100;
			padding: 5px 0;
			overflow: hidden;

			.drop-menu-action-wrapper {
				display: flex;
				align-items: center;
				position: relative;
				white-space: nowrap;

				.drop-menu-item {
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: 7px;
					padding: 3px 7px;
					width: 100%;
					color: gray;
					overflow: hidden;
					outline: none;

					.left-side {
						display: flex;
						align-items: center;
						gap: 7px;
						overflow: hidden;
					}

					.right-side {
						display: flex;
						align-items: center;
						gap: 7px;
						overflow: hidden;
					}

					.icon-holder {
						$size: 10px;

						min-width: $size;
						width: $size;
						font-size: 10px;
						text-shadow: 0 0 0 gray;
						color: transparent;

						img {
							min-width: $size;
							width: $size;
						}
					}

					&:not(.disabled) {
						cursor: pointer;
						color: inherit;

						&:focus {
							background-color: lighten($color: $medium-gray, $amount: 3);
							outline: 1px solid #656565;
						}

						&:hover {
							background: lighten($color: $medium-gray, $amount: 4);
						}
					}
				}

				.line-break {
					width: 100%;
					height: 1px;
					margin: 3px 7px;
					background: lighten($color: $medium-gray, $amount: 5);
				}
			}
		}
	}
}
</style>
